import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { useMutation } from '@tanstack/react-query';
import { useHandlers } from '../../../context/page-context';
import type { SyncLogIdentifier } from '../types';
import { useSyncApi } from './use-sync-api';
import { useSyncStatus } from './use-sync-log-status';

type UseTriggerManualSyncOptions = {
  /** Sync log to trigger, then refetch. Shares the status banner's query key, so the banner updates too. */
  syncFor: SyncLogIdentifier;
  /** Success toast message. Omit to stay silent on success; failures always raise a generic error toast. */
  successMessage?: string;
};

/** Triggers a manual sync for `syncFor`, then refetches its status. */
export const useTriggerManualSync = ({ syncFor, successMessage }: UseTriggerManualSyncOptions) => {
  const env = useEnv();
  const { notificationHandler } = useHandlers();
  const { syncApi } = useSyncApi();
  const { refetch: refetchSyncStatus } = useSyncStatus(syncFor);

  const { mutate: triggerManualSync, isPending: isSyncing } = useMutation({
    mutationFn: () =>
      syncApi.triggerManualSync({
        teamSlug: env.team.slugged_name,
        environment: env.environment,
        dataType: syncFor.dataType,
      }),
    onSuccess: () => {
      if (successMessage) {
        notificationHandler.success(successMessage);
      }
      return refetchSyncStatus();
    },
    onError: () => {
      notificationHandler.error();
    },
  });

  return { triggerManualSync, isSyncing };
};
